import React, { useMemo, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { brandColors } from '@config/brandTokens';

const REACTIONS = [
  { type: 'like', label: 'Like', On: ThumbUpAltIcon, Off: ThumbUpAltOutlinedIcon, color: brandColors.blue },
  { type: 'wish', label: 'Wish', On: AutoAwesomeIcon, Off: AutoAwesomeOutlinedIcon, color: '#7c3aed' },
  { type: 'love', label: 'Love', On: FavoriteIcon, Off: FavoriteBorderIcon, color: '#e11d48' },
  { type: 'improve', label: 'Improve', On: TrendingUpIcon, Off: TrendingUpIcon, color: '#0f766e' },
] as const;

type Counts = { like: number; wish: number; love: number; improve: number };
type Mine = { like: boolean; wish: boolean; love: boolean; improve: boolean };

export function getPublicVisitorId() {
  const key = 'encycgames_visitor_id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = globalThis.crypto?.randomUUID?.()
    || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const n = Math.floor(Math.random() * 16);
      const v = ch === 'x' ? n : (n & 0x3) | 0x8;
      return v.toString(16);
    });
  localStorage.setItem(key, created);
  return created;
}

export const VideoReactionBar: React.FC<{
  videoKey?: string | null;
  initial?: { counts?: Partial<Counts>; mine?: Partial<Mine> };
  dense?: boolean;
}> = ({ videoKey, initial, dense }) => {
  const [counts, setCounts] = useState<Counts>({
    like: Number(initial?.counts?.like || 0),
    wish: Number(initial?.counts?.wish || 0),
    love: Number(initial?.counts?.love || 0),
    improve: Number(initial?.counts?.improve || 0),
  });
  const [mine, setMine] = useState<Mine>({
    like: !!initial?.mine?.like,
    wish: !!initial?.mine?.wish,
    love: !!initial?.mine?.love,
    improve: !!initial?.mine?.improve,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const token = useMemo(
    () => localStorage.getItem('accessToken') || localStorage.getItem('token') || '',
    []
  );

  if (!videoKey) return null;

  const toggle = async (type: typeof REACTIONS[number]['type']) => {
    if (busy) return;
    setBusy(type);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch('/api/public/video-reactions', {
        method: 'POST',
        headers,
        body: JSON.stringify({ videoKey, reactionType: type, visitorId: getPublicVisitorId() }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) throw new Error(data?.message || 'Could not save');
      setCounts({
        like: Number(data.data?.counts?.like || 0),
        wish: Number(data.data?.counts?.wish || 0),
        love: Number(data.data?.counts?.love || 0),
        improve: Number(data.data?.counts?.improve || 0),
      });
      setMine({
        like: !!data.data?.mine?.like,
        wish: !!data.data?.mine?.wish,
        love: !!data.data?.mine?.love,
        improve: !!data.data?.mine?.improve,
      });
    } catch {
      /* keep last counts */
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack direction="row" spacing={dense ? 0 : 0.25} sx={{ mt: dense ? 0.15 : 0.5, flexWrap: 'wrap' }}>
      {REACTIONS.map((item) => {
        const active = mine[item.type];
        const Icon = active ? item.On : item.Off;
        return (
          <IconButton
            key={item.type}
            size="small"
            onClick={() => void toggle(item.type)}
            disabled={busy === item.type}
            title={item.label}
            sx={{
              borderRadius: 1.5,
              color: active ? item.color : brandColors.slate,
              p: dense ? 0.25 : undefined,
            }}
          >
            <Icon sx={{ fontSize: dense ? 14 : 18 }} />
            <Typography variant="caption" sx={{ ml: dense ? 0.2 : 0.35, fontWeight: 800, minWidth: 8, fontSize: dense ? 10 : undefined }}>
              {counts[item.type] || 0}
            </Typography>
          </IconButton>
        );
      })}
    </Stack>
  );
};

export default VideoReactionBar;
